import Client from '../../api/client';
import { buildWhereClause, buildOrderClauses } from './utils/sql-clause-builder';

interface LoadFileOptions {
  name?: string;
  id?: string;
  form_id?: string | null; // eslint-disable-line camelcase
  form_name?: string | null; // eslint-disable-line camelcase
  variable?: string | null;
}

interface LoadFormOptions {
  id: string | null;
  name: string | null;
}

export interface LoadRecordsOptions {
  ids?: string[];
  form_id?: string; // eslint-disable-line camelcase
  form_name?: string; // eslint-disable-line camelcase
  order?: string[][];
  where?: { operator?: string, predicates: any[] };
  limit?: number;
  offset?: number;
}

async function fetchFormIDByColumnValue(api: Client, columnName: string, columnValue): Promise<string | null> {
  const formID = (
    await api.query.run({
      q: `SELECT form_id FROM forms WHERE ${columnName} = '${columnValue}'`,
    })
  ).objects[0]?.form_id;

  return formID;
}

function fetchFormID(
  api: Client,
  id: string | undefined | null,
  name: string | undefined | null,
): Promise<string | null> {
  if (id) {
    return fetchFormIDByColumnValue(api, 'form_id', id);
  }

  return fetchFormIDByColumnValue(api, 'name', name);
}

async function fetchAttachmentByName(api: Client, formID: string, name: string | undefined, id: string | undefined) {
  const attachments = (
    // @ts-ignore-next-line
    await api.attachments.all({
      form_id: formID,
      owner_type: 'form',
    })
  ).objects;

  return attachments.find((attachment) => (attachment.id === id) || (attachment.name === name));
}

export async function loadFile(
  api: Client,
  options: LoadFileOptions,
  callback: (error: Error | null, result: any) => void,
) {
  let error: any = null;
  let result: string | null = null;
  try {
    const formID = await fetchFormID(api, options.form_id, options.form_name);

    if (!formID) {
      throw new Error('form not found');
    }

    const attachment = await fetchAttachmentByName(api, formID, options.name, options.id);

    if (!attachment) {
      throw new Error('file not found');
    }

    const response = await fetch(attachment.download_url);

    result = await response.text();
  } catch (ex: unknown) {
    error = buildError('LOADFILE() error', ex);
  }

  callback(error, result);
}

export async function loadForm(
  api: Client,
  options: LoadFormOptions,
  callback: (error: Error | null, result: any) => void,
) {
  let error: any = null;
  let result: any = null;

  try {
    const formID = await fetchFormID(api, options.id, options.name);

    if (!formID) {
      throw new Error('form not found');
    }

    // @ts-ignore-next-line
    result = { form: await api.forms.find(formID) };
  } catch (ex) {
    error = buildError('LOADFORM() error', ex);
  }

  callback(error, result);
}

export async function loadRecords(
  api: Client,
  options: LoadRecordsOptions,
  callback: (error: Error | null, result: any) => void,
) {
  let error: any = null;
  let result: any = null;

  try {
    const formID = await fetchFormID(api, options.form_id, options.form_name);

    if (!formID) {
      throw new Error('form not found');
    }

    let sql = recordSQL(formID);

    if (options.ids?.length || options.where) {
      sql += ' WHERE ';
      const conditions: string[] = [];

      if (options.ids?.length) {
        const idList = options.ids.map((id) => `'${id}'`).join(',');
        conditions.push(`records._record_id IN (${idList})`);
      }

      if (options.where) {
        const whereSql = await buildWhereClause(api, formID, options);
        conditions.push(whereSql);
      }

      sql += conditions.join(' AND ');
    }

    if (options.order) {
      const orderClauses = buildOrderClauses(options.order);

      sql += ` ORDER BY ${orderClauses}`;
    }

    if (options.limit) {
      sql += ` LIMIT ${options.limit}`;
    }

    if (options.offset) {
      sql += ` OFFSET ${options.offset}`;
    }

    const rows = (await api.query.run({ q: sql })).objects;

    const records = rows.map((row) => ({
      ...row,
      form_values: JSON.parse(row.form_values),
    }));

    result = { records };
  } catch (ex) {
    error = buildError('LOADRECORDS() error', ex);
  }

  callback(error, result);
}

function buildError(prefix: string, error: unknown) {
  const message = error instanceof Error ? error.message : 'unknown error';
  console.error(prefix, error);
  return { message };
}

function recordSQL(formID: string) {
  return `
  SELECT
    "_status" AS "status",
    "_version" AS "version",
    "_record_id" AS "id",
    to_char(pg_catalog.timezone('UTC', "_server_created_at"), 'YYYY-MM-DD"T"HH24:MI:SS"Z"') AS "created_at",
    to_char(pg_catalog.timezone('UTC', "_server_updated_at"), 'YYYY-MM-DD"T"HH24:MI:SS"Z"') AS "updated_at",
    to_char(pg_catalog.timezone('UTC', "_created_at"), 'YYYY-MM-DD"T"HH24:MI:SS"Z"') AS "client_created_at",
    to_char(pg_catalog.timezone('UTC', "_updated_at"), 'YYYY-MM-DD"T"HH24:MI:SS"Z"') AS "client_updated_at",
    "_created_by_id" AS "created_by_id",
    "_updated_by_id" AS "updated_by_id",
    '${formID}'::text AS "form_id",
    "_project_id" AS "project_id",
    "_assigned_to_id" AS "assigned_to_id",
    "_form_values" AS "form_values",
    "_geometry" AS "geometry",
    "_latitude" AS "latitude",
    "_longitude" AS "longitude",
    "_altitude" AS "altitude",
    "_speed" AS "speed",
    "_course" AS "course",
    "_horizontal_accuracy" AS "horizontal_accuracy",
    "_vertical_accuracy" AS "vertical_accuracy",
    "_edited_duration" AS "edited_duration",
    "_updated_duration" AS "updated_duration",
    "_created_duration" AS "created_duration",
    created_by.name AS "created_by",
    updated_by.name AS "updated_by",
    assigned_to.name AS "assigned_to",
    projects.name AS "project",
    "_changeset_id" AS "changeset_id",
    "_created_latitude" AS "created_latitude",
    "_created_longitude" AS "created_longitude",
    "_created_altitude" AS "created_altitude",
    "_created_horizontal_accuracy" AS "created_horizontal_accuracy",
    "_updated_latitude" AS "updated_latitude",
    "_updated_longitude" AS "updated_longitude",
    "_updated_altitude" AS "updated_altitude",
    "_updated_horizontal_accuracy" AS "updated_horizontal_accuracy"
  FROM "${formID}/_full" AS "records"
  LEFT OUTER JOIN projects ON records._project_id = projects.project_id
  LEFT OUTER JOIN memberships created_by ON records._created_by_id = created_by.user_id
  LEFT OUTER JOIN memberships updated_by ON records._updated_by_id = updated_by.user_id
  LEFT OUTER JOIN memberships assigned_to ON records._assigned_to_id = assigned_to.user_id
`;
}
